// MyMakeupFacesBlock — ô "số mặt tôi đánh" trên thẻ ca của thợ trang điểm
// (quyết định 0194 điều 5; chia theo NỮ · NAM · BÉ từ quyết định 0287).
//
// Vì sao khối này tồn tại: máy lương chia công mặt theo TỶ LỆ số mặt từng
// người, nhưng chỉ khi CẢ ê-kíp đã khai. Thiếu một lời khai là quay về chia
// đều — người đánh 4 mặt nhận bằng người đánh 1. Trước khối này, cửa tự khai
// đã mở ở máy chủ mà không màn nào gọi tới, nên đường ấy chưa dùng được ngoài
// đời.
//
// Từ QĐ 0287 người phân ê-kíp chia sẵn cho từng thợ bao nhiêu nữ / nam / bé;
// khối này hiện phần được chia và cho thợ SỬA nếu thực tế làm khác. Thợ gửi ba
// ô, máy chủ ghi tổng ba ô làm số mặt — máy lương vẫn đọc số tổng như cũ.
//
// Ba điều đã rào sẵn:
//
// 🔴 1. CHỈ hiện khi buổi thật sự ghi sổ số mặt (`facesLog`). Buổi tính công
//       theo cách khác mà bày ô này ra là mời thợ gõ một con số không đi đâu cả.
// 🔴 2. Số 0 là LỜI KHAI THẬT ("tôi không đánh mặt nào"), khác hẳn bỏ trống.
//       Nên cả ba ô rỗng thì nút Lưu mờ, chứ không lặng lẽ gửi 0.
// 🔴 3. KHÔNG hiện một đồng MỨC công nào, cùng lý lẽ với thẻ duyệt: mức nằm ở
//       Cơ chế lương, bày ở đây là dạy cả cơ sở cách tự tính tiền của nhau.
//
// Lời khai luôn về CHỜ DUYỆT sau mỗi lần lưu, kể cả buổi đã được duyệt trước
// đó — con số vừa đổi thì dấu duyệt cũ không còn nói về nó. Khối này nói rõ
// điều đó để thợ không tưởng mình vừa làm mất dấu duyệt.
import { useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/ui/badge";
import { Button } from "@/ui/button";
import { Input } from "@/ui/input";
import { useOrderWorkEntries } from "@/features/orders/quote/api";
import { useSetMyMakeupFaces, type MakeupFaceSplit } from "./api";

interface Props {
  orderId: string;
  /** staff.id của người đang đăng nhập (currentStaff.id). */
  myStaffId: string;
}

const MAX_FACES = 60;

type Key = keyof MakeupFaceSplit;
const KEYS: Key[] = ["female", "male", "child"];
const KEY_LABEL: Record<Key, string> = { female: "Nữ", male: "Nam", child: "Bé dưới 12" };

// Nhãn CỐ Ý trùng từng chữ với thẻ "Duyệt số mặt" bên màn Ca vận hành
// (`branch-shift/components/MakeupFacesReviewPanel.tsx`): thợ và người duyệt
// nhìn hai màn khác nhau về cùng một lời khai, nói lệch chữ là hai người tưởng
// đang nói về hai thứ. Và cố ý KHÔNG nêu tên chức danh — luật bài hướng dẫn
// trong app cấm dạy cả công ty ai duyệt cái gì.
const STATE_LABEL = {
  pending: "Chờ duyệt",
  approved: "Đã duyệt",
  rejected: "Đã trả lại",
} as const;

const STATE_BADGE = {
  pending: "yellow",
  approved: "green",
  rejected: "red",
} as const;

export function MyMakeupFacesBlock({ orderId, myStaffId }: Props) {
  const { data, isLoading } = useOrderWorkEntries(orderId);
  const saveM = useSetMyMakeupFaces(orderId);
  // `null` = chưa gõ gì ⇒ ô hiện lời khai trên máy chủ. Lưu xong đặt lại `null`
  // để ô đi theo con số vừa ghi.
  //
  // CỐ Ý không đồng bộ bằng `useEffect`: vừa bị lint chặn (setState trong
  // effect), vừa là cái bẫy thật — mỗi lần lưu máy trả về cả sổ nên effect
  // chạy lại và đè mất chữ người ta đang sửa.
  const [dangGo, setDangGo] = useState<Record<Key, string> | null>(null);

  const mine = data?.makeupFaces?.find((l) => l.staffId === myStaffId);
  const daKhai = typeof mine?.faces === "number" ? mine.faces : null;

  if (isLoading || !data?.facesLog) return null;

  // Dòng cũ khai theo TỔNG (chưa chia loại) thì hiện tổng ở ô Nữ — đúng cách
  // máy lương đang đếm (người lớn chưa rõ nam / nữ).
  const fromServer: Record<Key, string> = mine?.split
    ? {
        female: String(mine.split.female),
        male: String(mine.split.male),
        child: String(mine.split.child),
      }
    : { female: daKhai === null ? "" : String(daKhai), male: "", child: "" };
  const raw = dangGo ?? fromServer;

  const allEmpty = KEYS.every((k) => raw[k].trim() === "");
  const parsed = KEYS.map((k) => (raw[k].trim() === "" ? 0 : Number(raw[k])));
  const hopLe = !allEmpty && parsed.every((n) => Number.isInteger(n) && n >= 0 && n <= MAX_FACES);
  const loi =
    allEmpty || hopLe
      ? ""
      : parsed.some((n) => !Number.isInteger(n))
        ? "Số mặt phải là số nguyên"
        : parsed.some((n) => n < 0)
          ? "Số mặt không được âm"
          : `Mỗi ô nhiều nhất là ${MAX_FACES}`;
  const split: MakeupFaceSplit = { female: parsed[0] ?? 0, male: parsed[1] ?? 0, child: parsed[2] ?? 0 };
  const chuaDoi = dangGo === null;
  const review = data.facesReview;
  const want = data.orderFaces;

  return (
    // Khối có TÊN để đọc bằng trình đọc màn hình, và để ảnh chụp khoá đúng
    // khối này — chữ "Chờ duyệt" còn xuất hiện ở danh sách việc của cùng trang.
    <div
      role="group"
      aria-label="Khai số mặt trang điểm"
      className="mt-2.5 rounded-md border border-[var(--color-info)]/30 bg-[var(--color-info-bg)]/40 p-2.5"
    >
      <div className="mb-1.5 flex flex-wrap items-center gap-2">
        <span className="text-xs font-bold text-[var(--color-info-text)]">
          Số mặt bạn đánh ở ca này
        </span>
        {review ? (
          <Badge variant={STATE_BADGE[review.state]}>{STATE_LABEL[review.state]}</Badge>
        ) : null}
      </div>

      {want ? (
        <div className="mb-1.5 text-[10px] text-[var(--color-info-text)]/80">
          Đơn có {want.female} nữ · {want.male} nam · {want.child} bé dưới 12
        </div>
      ) : null}

      <div className="flex flex-wrap items-end gap-2">
        <div className="grid min-w-0 flex-1 grid-cols-3 gap-1.5">
          {KEYS.map((k) => (
            <label key={k} className="min-w-0">
              <span className="block text-[10px] text-[var(--color-info-text)]/80">{KEY_LABEL[k]}</span>
              <Input
                type="number"
                inputMode="numeric"
                min={0}
                max={MAX_FACES}
                step={1}
                value={raw[k]}
                aria-label={`Số mặt ${KEY_LABEL[k].toLowerCase()} bạn đánh ở ca này`}
                placeholder="0"
                onChange={(e) => setDangGo({ ...raw, [k]: e.target.value })}
                className="h-9 text-right tabular-nums"
              />
            </label>
          ))}
        </div>
        <Button
          type="button"
          size="sm"
          disabled={!hopLe || chuaDoi || saveM.isPending}
          onClick={() =>
            saveM.mutate(
              { split },
              {
                onSuccess: () => {
                  setDangGo(null);
                  toast.success("Đã gửi số mặt — chờ duyệt");
                },
                onError: (err: unknown) =>
                  toast.error(err instanceof Error ? err.message : "Không lưu được số mặt"),
              },
            )
          }
        >
          {saveM.isPending ? "Đang lưu…" : daKhai === null ? "Gửi" : "Cập nhật"}
        </Button>
      </div>
      {loi ? (
        <div role="alert" className="mt-1 text-[10px] font-semibold text-[var(--color-danger-text)]">
          {loi}
        </div>
      ) : null}

      <div className="mt-1.5 text-[10px] text-[var(--color-info-text)]/80">
        {daKhai === null
          ? "Chưa khai. Cả ê-kíp khai đủ và được duyệt thì công mặt mới chia theo số mặt từng người; còn thiếu một người là chia đều như cũ."
          : "Mỗi lần sửa số sẽ chuyển lại về chờ duyệt — đó là bình thường, không phải lỗi."}
      </div>

      {review?.state === "rejected" && review.rejectNote ? (
        <div className="mt-1.5 rounded border border-[var(--color-danger)]/30 bg-[var(--color-danger-bg)]/50 p-1.5 text-[10px] text-[var(--color-danger-text)]">
          Lý do trả lại: {review.rejectNote}
        </div>
      ) : null}
    </div>
  );
}
